/**
 * Word List Form Component
 *
 * Shared form for creating and editing word lists.
 */

import { useState, useEffect } from 'react';
import { useInitialData } from '../../../contexts/InitialDataContext';
import { getSkillLevelEnglishName } from '../../../services/initialDataApi';
import type {
  WordList,
  WordListCreateRequest,
  WordListUpdateRequest,
} from '../types/wordList.types';

interface WordListFormProps {
  wordList?: WordList;
  onSubmit: (data: WordListCreateRequest | WordListUpdateRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEdit?: boolean;
  contextLanguage?: string; // Language from URL context (for create mode)
  onFormChange?: (name: string, description: string, languageCode: string) => void;
  children?: React.ReactNode;
}

export function WordListForm({
  wordList,
  onSubmit,
  onCancel,
  isSubmitting,
  isEdit = false,
  contextLanguage,
  onFormChange,
  children,
}: WordListFormProps) {
  const { state: initialDataState } = useInitialData();
  const languages = initialDataState.data?.languages ?? [];
  const skillLevels = initialDataState.data?.skillLevels ?? [];
  const learningGoals = initialDataState.data?.learningGoals ?? [];

  // For create mode, use context language; for edit mode, use wordList language
  const effectiveLanguage = isEdit ? wordList?.languageCode : contextLanguage;

  const [name, setName] = useState(wordList?.name ?? '');
  const [description, setDescription] = useState(wordList?.description ?? '');
  const [type, setType] = useState(wordList?.type ?? 'custom');
  const [languageCode] = useState(effectiveLanguage ?? 'en');
  const [skillLevelId, setSkillLevelId] = useState(wordList?.skillLevelId ?? 3);
  const [thumbnailUrl, setThumbnailUrl] = useState(wordList?.thumbnailUrl ?? '');
  const [selectedLearningGoals, setSelectedLearningGoals] = useState<number[]>(
    wordList?.learningGoalIds ?? []
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (wordList) {
      setName(wordList.name);
      setDescription(wordList.description ?? '');
      setType(wordList.type);
      setSkillLevelId(wordList.skillLevelId);
      setThumbnailUrl(wordList.thumbnailUrl ?? '');
      setSelectedLearningGoals(wordList.learningGoalIds);
    }
  }, [wordList]);

  // Notify parent of form changes (for localizations panel)
  useEffect(() => {
    if (onFormChange) {
      onFormChange(name, description, languageCode);
    }
  }, [name, description, languageCode, onFormChange]);

  const handleLearningGoalToggle = (goalId: number) => {
    setSelectedLearningGoals((prev) =>
      prev.includes(goalId) ? prev.filter((id) => id !== goalId) : [...prev, goalId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (!type) newErrors.type = 'Type is required';
    if (!languageCode) newErrors.languageCode = 'Language is required';
    if (!skillLevelId) newErrors.skillLevelId = 'Skill level is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (isEdit) {
      const updateData: WordListUpdateRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        skill_level_id: skillLevelId,
        thumbnail_url: thumbnailUrl.trim() || undefined,
      };
      onSubmit(updateData);
    } else {
      const createData: WordListCreateRequest & { learningGoalIds?: number[] } = {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        language_code: languageCode,
        skill_level_id: skillLevelId,
        thumbnail_url: thumbnailUrl.trim() || undefined,
        learningGoalIds: selectedLearningGoals.length > 0 ? selectedLearningGoals : undefined,
      };
      onSubmit(createData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Language - read-only, set by context */}
      <div>
        <label htmlFor="languageCode" className="block text-sm font-medium text-gray-700">
          Language
        </label>
        <input
          type="text"
          id="languageCode"
          value={languages.find((l) => l.code === languageCode)?.name || languageCode}
          disabled
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 cursor-not-allowed text-gray-600"
        />
        <p className="mt-1 text-sm text-gray-500">
          Language is set by the context dropdown. Go back to change it.
        </p>
      </div>

      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., IELTS Academic 500"
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Describe the word list..."
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Type */}
      <div>
        <label htmlFor="type" className="block text-sm font-medium text-gray-700">
          Type <span className="text-red-500">*</span>
        </label>
        <select
          id="type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
            errors.type ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="exams">Exams</option>
          <option value="topics">Topics</option>
          <option value="frequency">Frequency</option>
          <option value="custom">Custom</option>
        </select>
        {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type}</p>}
      </div>

      {/* Skill Level */}
      <div>
        <label htmlFor="skillLevelId" className="block text-sm font-medium text-gray-700">
          Skill Level <span className="text-red-500">*</span>
        </label>
        <select
          id="skillLevelId"
          value={skillLevelId}
          onChange={(e) => setSkillLevelId(Number(e.target.value))}
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${
            errors.skillLevelId ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          {skillLevels.map((level) => (
            <option key={level.id} value={level.id}>
              {getSkillLevelEnglishName(level)}
            </option>
          ))}
        </select>
        {errors.skillLevelId && <p className="mt-1 text-sm text-red-600">{errors.skillLevelId}</p>}
      </div>

      {/* Thumbnail URL */}
      <div>
        <label htmlFor="thumbnailUrl" className="block text-sm font-medium text-gray-700">
          Thumbnail URL
        </label>
        <input
          type="url"
          id="thumbnailUrl"
          value={thumbnailUrl}
          onChange={(e) => setThumbnailUrl(e.target.value)}
          placeholder="https://example.com/image.jpg"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Learning Goals */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Learning Goals</label>
        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
          {learningGoals.map((goal) => (
            <label key={goal.id} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedLearningGoals.includes(goal.id)}
                onChange={() => handleLearningGoalToggle(goal.id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">
                {goal.icon_emoji ? `${goal.icon_emoji} ` : ''}
                {goal.name}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Children (e.g., localizations panel) */}
      {children}

      {/* Actions */}
      <div className="flex justify-end space-x-4 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : isEdit ? 'Update Word List' : 'Create Word List'}
        </button>
      </div>
    </form>
  );
}
